import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, Image } from '@/components/ui/image';
import ShareButton from '@/components/common/ShareButton';
import { ArrowLeft, Heart } from 'lucide-react';
import { formatLocalDateTime } from '@/utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PostDetailPage = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchPost();
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  const fetchPost = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/posts/${postId}`, { headers });
      setPost(res.data);
    } catch (err) {
      toast.error('帖子不存在');
      navigate('/community');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API}/posts/${postId}/comments`);
      setComments(res.data);
    } catch (err) {
      console.error('Failed to fetch comments', err);
    }
  };

  const handleComment = async () => {
    if (!user) {
      toast.error('请先登录');
      navigate('/login');
      return;
    }
    if (!newComment.trim()) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/posts/${postId}/comments`, { content: newComment }, { headers });
      setNewComment('');
      fetchComments();
      fetchPost();
      toast.success('评论成功');
    } catch (err) {
      toast.error('评论失败');
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast.error('请先登录');
      navigate('/login');
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/posts/${postId}/like`, {}, { headers });
      setPost({ ...post, is_liked: res.data.liked, likes_count: res.data.likes_count });
    } catch (err) {
      toast.error('操作失败');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-amber-50/50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-amber-50/50 py-8" data-testid="post-detail-page">
      <div className="container mx-auto px-4 max-w-3xl">
        <Button variant="ghost" onClick={() => navigate('/community')} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('community.backToCommunity', '返回社区')}
        </Button>

        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Avatar
                src={post.user_avatar}
                name={post.user_name}
                className="w-12 h-12 cursor-pointer"
                onClick={() => navigate(`/profile/${post.user_id}`)}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-semibold text-amber-800">{post.user_name}</span>
                  <span className="text-sm text-amber-600">{formatLocalDateTime(post.created_at)}</span>
                </div>
                <p className="text-amber-700 whitespace-pre-wrap mb-4">{post.content}</p>
                {post.images && post.images.length > 0 && (
                  <div className="grid gap-2 mb-4">
                    {post.images.map((img, idx) => (
                      <Image key={idx} src={img} alt="" className="w-full rounded-lg" />
                    ))}
                  </div>
                )}
                {post.video_url && (
                  <video src={post.video_url} controls className="w-full rounded-lg mb-4" />
                )}
                {post.tags && post.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.map((tag) => (
                      <Badge key={tag} variant="outline">#{tag}</Badge>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-6 text-amber-600">
                  <button className={`flex items-center gap-1 ${post.is_liked ? 'text-red-500' : ''}`} onClick={handleLike}>
                    <Heart className={`w-5 h-5 ${post.is_liked ? 'fill-current' : ''}`} /> {post.likes_count}
                  </button>
                  <span>💬 {post.comments_count}</span>
                  <ShareButton
                    url={`/community/post/${postId}`}
                    title={post.content?.substring(0, 100) || t('community.title')}
                    description={post.content?.substring(0, 200) || ''}
                    image={post.images?.[0] || ''}
                    variant="ghost"
                    size="sm"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Comments */}
        <Card>
          <CardHeader>
            <CardTitle>{t('community.comments', '评论')} ({comments.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 mb-6">
              <Textarea
                placeholder={user ? t('community.writeComment', '写下你的评论...') : t('community.loginToComment', '登录后可以评论')}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                disabled={!user}
              />
              <Button onClick={handleComment} disabled={!user} className="bg-amber-600 hover:bg-amber-700">
                {t('community.send', '发送')}
              </Button>
            </div>
            <div className="space-y-4">
              {comments.map((comment) => (
                <div key={comment.id} className="flex gap-3 p-4 bg-amber-50/50 rounded-lg">
                  <Avatar
                    src={comment.user_avatar}
                    name={comment.user_name}
                    className="w-10 h-10"
                  />
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{comment.user_name}</span>
                      <span className="text-xs text-amber-600">{formatLocalDateTime(comment.created_at)}</span>
                    </div>
                    <p className="text-amber-700">{comment.content}</p>
                  </div>
                </div>
              ))}
              {comments.length === 0 && (
                <p className="text-center text-amber-600 py-8">{t('community.noComments', '暂无评论，来抢沙发吧~')}</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PostDetailPage;
